import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import axios from 'utils/axios';
import useAuth from 'hooks/useAuth';

// types
import { Company, CompanyMembership, CompanyUnitPreference } from 'types/company';
import { Item } from 'types/item';

// api helpers
import { getItemTranslation, getItemPrice, getItemDisplayName, formatItemForDisplay } from 'api/item';

interface CompanyState {
  companies: CompanyMembership[];
  activeCompany: Company | null;
  companyUnits: CompanyUnitPreference[];
  companyItems: Item[];
  loading: boolean;
  unitsLoading: boolean;
  itemsLoading: boolean;
  error: string | null;
  unitsError: string | null;
  itemsError: string | null;
}

type CompanyAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_UNITS_LOADING'; payload: boolean }
  | { type: 'SET_ITEMS_LOADING'; payload: boolean }
  | { type: 'SET_COMPANIES'; payload: CompanyMembership[] }
  | { type: 'SET_ACTIVE_COMPANY'; payload: Company | null }
  | { type: 'SET_COMPANY_UNITS'; payload: CompanyUnitPreference[] }
  | { type: 'SET_COMPANY_ITEMS'; payload: Item[] }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_UNITS_ERROR'; payload: string | null }
  | { type: 'SET_ITEMS_ERROR'; payload: string | null }
  | { type: 'RESET' };

// Initial state
const initialState: CompanyState = {
  companies: [],
  activeCompany: null,
  companyUnits: [],
  companyItems: [],
  loading: false,
  unitsLoading: false,
  itemsLoading: false,
  error: null,
  unitsError: null,
  itemsError: null
};

// Reducer
function companyReducer(state: CompanyState, action: CompanyAction): CompanyState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_UNITS_LOADING':
      return { ...state, unitsLoading: action.payload };
    case 'SET_ITEMS_LOADING':
      return { ...state, itemsLoading: action.payload };
    case 'SET_COMPANIES':
      return { ...state, companies: action.payload, loading: false };
    case 'SET_ACTIVE_COMPANY':
      return { ...state, activeCompany: action.payload };
    case 'SET_COMPANY_UNITS':
      return { ...state, companyUnits: action.payload, unitsLoading: false };
    case 'SET_COMPANY_ITEMS':
      return { ...state, companyItems: action.payload, itemsLoading: false };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_UNITS_ERROR':
      return { ...state, unitsError: action.payload, unitsLoading: false };
    case 'SET_ITEMS_ERROR':
      return { ...state, itemsError: action.payload, itemsLoading: false };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// Context
interface CompanyContextType extends CompanyState {
  setActiveCompany: (company: Company) => void;
  refreshCompanies: () => Promise<void>;
  refreshCompanyUnits: () => Promise<void>;
  refreshCompanyItems: () => Promise<void>;
  getItemTranslation: typeof getItemTranslation;
  getItemPrice: typeof getItemPrice;
  getItemDisplayName: typeof getItemDisplayName;
  formatItemForDisplay: typeof formatItemForDisplay;
  hasCompanies: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

// Provider
interface CompanyProviderProps {
  children: ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const [state, dispatch] = useReducer(companyReducer, initialState);
  const { isLoggedIn, user } = useAuth();

  // Load companies when user logs in
  useEffect(() => {
    if (isLoggedIn && user) {
      loadCompanies();
    } else {
      dispatch({ type: 'RESET' });
      localStorage.removeItem('activeCompanyId');
    }
  }, [isLoggedIn, user]);

  // Load active company from localStorage on mount
  useEffect(() => {
    if (state.companies.length > 0) {
      const savedCompanyId = localStorage.getItem('activeCompanyId');
      if (savedCompanyId) {
        const savedCompany = state.companies.find((membership) => membership.company?.id === parseInt(savedCompanyId));
        if (savedCompany?.company) {
          dispatch({ type: 'SET_ACTIVE_COMPANY', payload: savedCompany.company });
          return;
        }
      }

      // If no saved company or saved company not found, set first company as active
      if (state.companies.length === 1 && state.companies[0]?.company) {
        setActiveCompany(state.companies[0].company);
      }
    }
  }, [state.companies]);

  // Load company units and items when active company changes
  useEffect(() => {
    if (state.activeCompany?.id) {
      loadCompanyUnits(state.activeCompany.id);
      loadCompanyItems(state.activeCompany.id);
    } else {
      dispatch({ type: 'SET_COMPANY_UNITS', payload: [] });
      dispatch({ type: 'SET_COMPANY_ITEMS', payload: [] });
    }
  }, [state.activeCompany?.id]);

  const loadCompanies = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await axios.get('/api/users/companies/');

      // Validate response data
      const companies = Array.isArray(response.data) ? response.data : [];

      dispatch({ type: 'SET_COMPANIES', payload: companies });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error: any) {
      console.error('Error loading companies:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to load companies' });
    }
  };

  const loadCompanyUnits = async (companyId: number) => {
    try {
      dispatch({ type: 'SET_UNITS_LOADING', payload: true });
      dispatch({ type: 'SET_UNITS_ERROR', payload: null });

      // Updated endpoint: /unit-preferences instead of /company-unit-preferences
      const response = await axios.get(`/api/companies/unit-preferences/?company=${companyId}`);

      // Extract results from paginated response
      const units = response.data?.results || [];

      dispatch({ type: 'SET_COMPANY_UNITS', payload: units });
    } catch (error: any) {
      console.error('Error loading company units:', error);
      dispatch({
        type: 'SET_UNITS_ERROR',
        payload: error.message || 'Failed to load company unit preferences'
      });
      // Set empty array on error so components can use fallback units
      dispatch({ type: 'SET_COMPANY_UNITS', payload: [] });
    }
  };

  const loadCompanyItems = async (companyId: number) => {
    try {
      dispatch({ type: 'SET_ITEMS_LOADING', payload: true });
      dispatch({ type: 'SET_ITEMS_ERROR', payload: null });

      const response = await axios.get(`/api/items/?company=${companyId}`);

      // Extract results from paginated response or use array directly
      const items = response.data?.results || response.data || [];

      dispatch({ type: 'SET_COMPANY_ITEMS', payload: items });
    } catch (error: any) {
      console.error('Error loading company items:', error);
      dispatch({
        type: 'SET_ITEMS_ERROR',
        payload: error.message || 'Failed to load company items'
      });
      // Set empty array on error so components can handle gracefully
      dispatch({ type: 'SET_COMPANY_ITEMS', payload: [] });
    }
  };

  const setActiveCompany = (company: Company | null) => {
    if (!company) {
      dispatch({ type: 'SET_ACTIVE_COMPANY', payload: null });
      localStorage.removeItem('activeCompanyId');
      return;
    }

    // Validate company object has required fields
    if (!company.id) {
      console.error('Company object missing id:', company);
      return;
    }

    dispatch({ type: 'SET_ACTIVE_COMPANY', payload: company });
    localStorage.setItem('activeCompanyId', company.id.toString());
  };

  const refreshCompanies = async () => {
    await loadCompanies();
  };

  const refreshCompanyUnits = async () => {
    if (state.activeCompany?.id) {
      await loadCompanyUnits(state.activeCompany.id);
    }
  };

  const refreshCompanyItems = async () => {
    if (state.activeCompany?.id) {
      await loadCompanyItems(state.activeCompany.id);
    }
  };

  const value: CompanyContextType = {
    ...state,
    setActiveCompany,
    refreshCompanies,
    refreshCompanyUnits,
    refreshCompanyItems,
    getItemTranslation,
    getItemPrice,
    getItemDisplayName,
    formatItemForDisplay,
    hasCompanies: state.companies.length > 0
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

// Hook
export default function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
